import { Box, Typography } from "@material-ui/core";
import { TFunction, useTranslation } from "react-i18next";
import FlexTypographyComponent from "../../../../components/FlexTypographyComponent";
import FlexTypographyArray from "../../../../components/LyfeTable/FlexTypographyArray";
import LyfeAvatar from "../../../../components/LyfeAvatar";
import MappedComponent from "../../../../components/mapped-components/MappedComponent";
import { tableIdMinWidth } from "../../../../hooks/use-map-theme-styles";
import { EMPTY_VALUE_PLACEHOLDER } from "../../../../models/common/data";
import { Column } from "../../../../types/tableTypes";
import { getPurchaseOrPatientFieldName } from "../../../../utils/getCaselessOrPatientFields";
import { localeDate } from "../../../../utils/locale-date";
import { splitName } from "../../../../utils/name-to-initials";
import { GetRecordsQuery } from "../../types/queries.generated";

type RecordColumn = Column<GetRecordsQuery["records"][number]>;

export function getRecordColumns(
	t: TFunction<string>,
	hasOnlyPatientAgreements: boolean,
	hasOnlyCaselessAgreements: boolean,
	dateLabel: string
): Array<RecordColumn> {
	return [
		{
			title: t("record-id"),
			field: "id",
			default: true,
			render: (rawData: any) => (
				<Typography style={{ width: tableIdMinWidth }} variant="body2">
					{rawData.id}
				</Typography>
			),
		},
		{
			title: t("type"),
			field: "flexEntityName",
			default: true,
			render: (rowData) => <FlexTypographyComponent text={t(rowData.flexEntityName!)} />,
		},
		{
			title: t("status"),
			field: "status",
			default: true,
			render: (rowData) => <MappedComponent component="RecordStatus" props={rowData} />,
		},
		{
			title: getPurchaseOrPatientFieldName(hasOnlyPatientAgreements, hasOnlyCaselessAgreements, t),
			field: "purchaseOrPatientId",
			default: true,
			render: (rowData) => <FlexTypographyComponent text={rowData?.purchaseOrPatientId ?? EMPTY_VALUE_PLACEHOLDER} />,
		},
		{
			title: dateLabel,
			field: "viewDate",
			default: true,
			render: (rowData) => <FlexTypographyComponent text={localeDate(rowData.viewDate)} />,
		},
		{
			title: t("product"),
			field: "preparationBrandNames",
			default: true,
			render: (rowData) => <FlexTypographyArray rowData={rowData} fieldAssessor="preparationBrandNames" />,
		},
		{
			title: t("cases"),
			field: "viewCases",
			default: true,
			render: (rowData) => <FlexTypographyArray rowData={rowData} fieldAssessor="viewCases" />,
		},
		{
			title: t("added-on"),
			field: "createdOn",
			default: true,
			align: "right",
			render: (rowData) => (
				<FlexTypographyComponent containerStyles={{ justifyContent: "flex-end" }} text={localeDate(rowData?.createdOn)!} />
			),
		},
		{
			title: t("added-by"),
			field: "createdByName",
			default: true,
			render: (rowData) => (
				<Box width="100%">
					<LyfeAvatar
						firstName={splitName(rowData.createdByName!)[0] || ""}
						lastName={splitName(rowData.createdByName!)[1] || ""}
						size="small"
						initialsProps={{ style: { fontSize: "14px" } }}
						userEmail={rowData.createdByEmail!}
					/>
				</Box>
			),
		},
	];
}

export default function useRecordColumns(
	hasOnlyPatientAgreements: boolean,
	hasOnlyCaselessAgreements: boolean,
	dateLabel: string
): { columns: Array<RecordColumn> } {
	const { t } = useTranslation();
	const columns = getRecordColumns(t, hasOnlyPatientAgreements, hasOnlyCaselessAgreements, dateLabel);

	return { columns };
}
